import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Channel } from 'lib-shared-types-channels';

export type PresenceUpdate = {
  is_online?: boolean;
  is_working?: boolean;
  simultaneous_ring?: boolean;
  working_media_types?: string[];
  workloads_by_channel?: Partial<Record<Channel, number>>;
  has_acw?: boolean;
  custom_away_status?: {
    custom_away_status_id: string;
    status_updated_at: string;
  };
  display_name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  additional_emails?: string[];
  additional_phone_numbers?: string[];
};

type PresenceOverlayState = {
  members: Record<string, PresenceUpdate>;
};

const initialState: PresenceOverlayState = {
  members: {},
};

export const derivePresenceFlags = (
  presenceStatus: string | undefined,
): Pick<PresenceUpdate, 'is_online' | 'is_working'> => {
  const normalized = presenceStatus?.toLowerCase();

  switch (normalized) {
    case 'working':
      return { is_working: true, is_online: true };
    case 'away':
      return { is_working: false, is_online: true };
    case 'offline':
    default:
      return { is_working: false, is_online: false };
  }
};

export const presenceOverlaySlice = createSlice({
  name: 'presenceOverlay',
  initialState,
  reducers: {
    updateMemberPresence: (
      state,
      action: PayloadAction<{ memberId: string; updates: PresenceUpdate }>,
    ) => {
      const { memberId, updates } = action.payload;
      state.members[memberId] = {
        ...state.members[memberId],
        ...updates,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (
        action,
      ): action is PayloadAction<{
        params: { user_id: string; new_status: string };
      }> =>
        action.type === 'PRESENCE_WS_SET_OWN_STATUS' ||
        action.type === 'PRESENCE_WS_AGENT_STATUS_CHANGED',
      (state, action) => {
        const params = action.payload?.params;
        if (!params?.user_id || !params?.new_status) return;
        const flags = derivePresenceFlags(params.new_status);
        state.members[params.user_id] = {
          ...state.members[params.user_id],
          ...flags,
        };
      },
    );
  },
});

export const { updateMemberPresence } = presenceOverlaySlice.actions;

export const presenceOverlayReducer = presenceOverlaySlice.reducer;
